import React, { useEffect, useRef, useState } from 'react';
import { useInView } from './Section';

// Stepped giant text reveal in three phases like dayos hero scroll
const PHASES = [
  { num: '01', word: 'TALENT.', body: 'Talent is everywhere. Singers, musicians, performers, creators, all working harder than the industry has ever made it easy for them to win.' },
  { num: '02', word: 'GAP.', body: '90% of independent musicians never receive formal business training. The industry stays fragmented, often toxic, and built on gatekeeping.' },
  { num: '03', word: 'CLOSED.', body: 'SAMZ closes the gap with one ecosystem: academy, marketplace, rallies, and network. Education leads to opportunity. Opportunity leads to income. Income leads to independence.' },
];

export default function PinnedReveal() {
  const wrapRef = useRef(null);
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onScroll = () => {
      const rect = wrap.getBoundingClientRect();
      const total = wrap.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      const idx = Math.min(PHASES.length - 1, Math.floor(p * PHASES.length));
      setActive(idx);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isMobile]);

  if (isMobile) {
    return (
      <section className="relative bg-black py-20" data-testid="pinned-reveal-mobile">
        <div className="px-6 flex flex-col gap-14">
          {PHASES.map((p) => (
            <div key={p.num}>
              <span className="pin-num text-[#22A773]">{p.num}</span>
              <h2 className="giant-text text-white mt-3">{p.word}</h2>
              <p className="text-white/70 text-[15px] leading-[1.7] mt-4">{p.body}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={wrapRef} className="relative bg-black" style={{ height: '300vh' }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center">
        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-6 mb-10">
              {PHASES.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`pin-num transition-colors duration-300 ${i === active ? 'text-[#22A773]' : 'text-white/30'}`}>{p.num}</span>
                </div>
              ))}
            </div>
            <h2 className="giant-text text-white relative h-[1.05em] overflow-hidden">
              {PHASES.map((p, i) => (
                <span
                  key={i}
                  className="absolute inset-0 transition-all duration-700"
                  style={{
                    transform: i === active ? 'translateY(0%)' : i < active ? 'translateY(-110%)' : 'translateY(110%)',
                    opacity: i === active ? 1 : 0,
                  }}
                >
                  {p.word}
                </span>
              ))}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <div className="relative min-h-[180px]">
              {PHASES.map((p, i) => (
                <p
                  key={i}
                  className="absolute inset-0 text-white/70 text-[16px] leading-[1.7] transition-all duration-700"
                  style={{
                    transform: i === active ? 'translateY(0)' : 'translateY(20px)',
                    opacity: i === active ? 1 : 0,
                  }}
                >
                  {p.body}
                </p>
              ))}
            </div>
            <div className="mt-10 flex items-center gap-3">
              {PHASES.map((_, i) => (
                <div key={i} className="w-12 h-[2px] bg-white/10 overflow-hidden rounded-full">
                  <div
                    className="h-full bg-[#22A773] transition-all duration-500"
                    style={{ width: i <= active ? '100%' : '0%' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
